import { FC, useEffect, useState } from 'react';

import AddressFinder, {
  defaultValues as addressFinderDefaultValues,
  addressFinderSchema,
} from '@eneco/components/AddressFinder/AddressFinder';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { ApiError } from '@eneco/dc/client/ApiError';
import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Stack, VisuallyHidden } from '@eneco/sparky';
import { Customers_Address_AddressModel } from '@monorepo-types/dc';
import { usePlaceholderContent } from '@sitecore/common';
import { AddressStepRendering, Fields } from '@sitecore/types/AddressStep';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextServiceGemak } from '../../context';

interface FormFields {
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  city: string;
  street: string;
}
export const generateErrorNotificationBodyFromApi = (err?: ApiError, textualData?: Fields) => {
  if (!err?.status) {
    return null;
  }
  if (err.status === 500) {
    return textualData?.notification?.serverErrorNotification;
  }
  return textualData?.notification?.notFoundErrorNotification;
};
export const AddressStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextServiceGemak>();
  const sendToFlowMachine = useFlowActorRef().send;
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);
  const [errorData, setErrorData] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);
  const [addressFinderData, setAddressFinderData] = useState<Customers_Address_AddressModel>();

  const context = useFlowSelector(state => state.context);
  const { postalCode, houseNumber, houseNumberSuffix, street, city } = context;

  const { AddressStep: { fields: textualData } = {} } = usePlaceholderContent<{ AddressStep: AddressStepRendering }>();

  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
  });

  const formMethods = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      ...addressFinderDefaultValues,
      postalCode,
      houseNumber,
      houseNumberSuffix,
      street,
      city,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const {
    handleSubmit,
    getValues,
    formState: { isSubmitSuccessful },
  } = formMethods;

  useEffect(() => {
    if (isSubmitSuccessful && !errorData && addressFinderData) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...addressFinderData,
          ...formData,
          houseNumber: Number(formData.houseNumber),
          houseNumberSuffix: formData.houseNumberSuffix || undefined,
          sgProduct: context.sgProduct ?? context.sgDefault,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const errorContent = generateErrorNotificationBodyFromApi(errorData, textualData);

  return (
    <FormProvider {...formMethods}>
      <Layout.Content variant="A" handleSubmit={handleSubmit}>
        <StepHeader textualData={textualData} />
        <Stack.Item as="fieldset">
          <VisuallyHidden>
            <legend>{textualData?.content?.addressGroupLabel?.value ?? 'adres invoer'}</legend>
          </VisuallyHidden>
          <AddressFinder
            isSuffixRequired={isSuffixRequired}
            setIsSuffixRequired={setIsSuffixRequired}
            customLabels={{
              houseNumber: textualData?.address?.houseNumberFormField,
              houseNumberSuffix: textualData?.address?.houseNumberSuffixFormField,
              postalCode: textualData?.address?.postalCodeFormField,
            }}
            setErrorData={setErrorData}
            setIsLoading={setIsLoading}
            setAddressFinderData={setAddressFinderData}
            customErrorNotification={
              <TrackedNotificationBox
                emphasis="low"
                isAlert
                title={errorContent?.value?.title || ''}
                text={errorContent?.value?.content && <RichText html={errorContent?.value?.content} />}
                variant="error"
              />
            }
          />
        </Stack.Item>

        <NextButton isLoading={isSubmitSuccessful || isLoading}>{textualData?.content?.nextStepText?.value}</NextButton>
      </Layout.Content>
    </FormProvider>
  );
};
